import { FC, useCallback, useEffect } from 'react';
import { Controller, ResolverOptions, useForm } from 'react-hook-form';
import { useFetcher, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { parse, isValid, isAfter, subYears, startOfDay } from 'date-fns';
import { z } from 'zod';
import { PatientIdField } from '@samd/features/orders/components/patient_id_form_field';
import {
  SamdPatientPhiFormSchema,
  SamdPatientPhiFormSection,
} from '@samd/features/orders/components/patient_phi_form_section';
import { IntegerInput } from 'src/components/integer_input';
import { SelectList } from 'src/components/select_list';
import {
  GleasonScoreFields,
  OPTIONAL_GLEASON_SCORE_FIELDS_SCHEMA,
} from 'src/distribution_platforms/features/components/gleason_score_form_fields';
import {
  emptyToUndefined,
  FormActions,
  FormFieldProps,
  processFetcherResponse,
  getProstateBiopsyTStageOptions,
  PROSTATE_BIOPSY_T_STAGES,
  safeEmptyToUndefined,
  safeParseInt,
  submitOrderAction,
} from 'src/distribution_platforms/features/order_form';
import {
  SamdOrderDetailFieldsFragment,
  OrderStatus,
  SpecimenType,
  TStage,
} from 'src/types';
import { OrderAction } from 'src/types/loaders';
import { notifyError, notifySuccess, SpecimenTypeUtils } from 'src/utils';

const FORM_CONSTANTS = {
  WIDTH: '840px',
  MIN_AGE: 55,
  MAX_AGE: 98,
} as const;

export type DemoProstateBiopsyFormData = z.infer<
  typeof DemoProstateBiopsyFormSchema
>;

export const DemoProstateBiopsyFormSchema = z.object({
  externalId: z.string().min(1, 'Patient ID is required'),
  ...SamdPatientPhiFormSchema.shape,
  dateOfBirth: z
    .string()
    .min(1, 'Date of Birth is required')
    .refine(
      (val) => {
        if (!val) return false;
        try {
          const date = parse(val, 'yyyy-MM-dd', new Date());
          if (!isValid(date)) return false;

          const today = startOfDay(new Date());
          const minDate = subYears(today, FORM_CONSTANTS.MAX_AGE);
          const maxDate = subYears(today, FORM_CONSTANTS.MIN_AGE);

          return isAfter(date, minDate) && !isAfter(date, maxDate);
        } catch {
          return false;
        }
      },
      {
        message: `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
      },
    ),
  patientAge: z.preprocess(
    (val) => {
      const num = typeof val === 'string' ? parseInt(val) : val;
      return num;
    },
    z
      .number({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return { message: 'Patient age is required' };
          }

          return null;
        },
      })
      .min(
        FORM_CONSTANTS.MIN_AGE,
        `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
      )
      .max(
        FORM_CONSTANTS.MAX_AGE,
        `Acceptable age range for patient is from ${FORM_CONSTANTS.MIN_AGE} to ${FORM_CONSTANTS.MAX_AGE}`,
      ),
  ) as z.ZodPipe<z.ZodTransform<number, number>, z.ZodNumber>,
  ...OPTIONAL_GLEASON_SCORE_FIELDS_SCHEMA.shape,
  tStage: z.enum(PROSTATE_BIOPSY_T_STAGES).optional(),
});

const createCustomResolver =
  () =>
  async (
    data: DemoProstateBiopsyFormData,
    context: unknown,
    options: ResolverOptions<DemoProstateBiopsyFormData>,
  ) => {
    return zodResolver(DemoProstateBiopsyFormSchema)(data, context, options);
  };

const createDefaultFormValues = (
  initialOrder: SamdOrderDetailFieldsFragment,
): DemoProstateBiopsyFormData => ({
  externalId: initialOrder.externalId?.toString() ?? '',
  firstName: initialOrder.patient?.firstNamePhi ?? '',
  lastName: initialOrder.patient?.lastNamePhi ?? '',
  dateOfBirth: initialOrder.patient?.dateOfBirthPhi ?? '',
  patientAge: (initialOrder.patientAge ?? '') as number,
  primaryGleasonScore: initialOrder.primaryGleasonScore?.toString() as
    | '3'
    | '4'
    | '5'
    | undefined,
  secondaryGleasonScore: initialOrder.secondaryGleasonScore?.toString() as
    | '3'
    | '4'
    | '5'
    | undefined,
  tStage: initialOrder.tStage || undefined,
});

const PatientAgeField: FC<FormFieldProps<DemoProstateBiopsyFormData>> = ({
  control,
  errors,
}) => (
  <Controller
    name='patientAge'
    control={control}
    render={({ field }) => (
      <IntegerInput
        required
        label='Age at Diagnosis'
        data-testid='patientAge'
        {...field}
        error={!!errors.patientAge}
        helperText={errors.patientAge?.message?.toString() ?? ' '}
        min={FORM_CONSTANTS.MIN_AGE}
        max={FORM_CONSTANTS.MAX_AGE}
      />
    )}
  />
);

const TStageField: FC<FormFieldProps<DemoProstateBiopsyFormData>> = ({
  control,
  errors,
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      gap: { xs: 2, md: 3 },
    }}
  >
    <Controller
      name='tStage'
      control={control}
      render={({ field }) => (
        <SelectList
          labelId='t-stage-label'
          data-testid='t-stage'
          label='T-stage'
          {...field}
          options={getProstateBiopsyTStageOptions(false, false)}
          validationText={errors.tStage?.message?.toString() ?? ''}
          onChange={field.onChange}
        />
      )}
    />
  </Box>
);

export const DemoSamdProstateBiopsyOrderForm: FC<{
  initialOrder: SamdOrderDetailFieldsFragment;
  onClose: () => void;
}> = ({ initialOrder, onClose }) => {
  const theme = useTheme();
  const fetcher = useFetcher();
  const location = useLocation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<DemoProstateBiopsyFormData>({
    resolver: createCustomResolver(),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: createDefaultFormValues(initialOrder),
  });

  const getOrderFromFormData =
    useCallback((): SamdOrderDetailFieldsFragment => {
      const formData = getValues();

      return {
        ...initialOrder,
        externalId: emptyToUndefined(formData.externalId) as string,
        patient: {
          firstNamePhi: formData.firstName.trim() || null,
          lastNamePhi: formData.lastName.trim() || null,
          dateOfBirthPhi: formData.dateOfBirth || null,
        },
        patientAge:
          typeof formData.patientAge === 'string'
            ? safeParseInt(formData.patientAge)
            : formData.patientAge || undefined,
        primaryGleasonScore: safeParseInt(formData.primaryGleasonScore),
        secondaryGleasonScore: safeParseInt(formData.secondaryGleasonScore),
        tStage: safeEmptyToUndefined(formData.tStage) as TStage | undefined,
        specimenType: SpecimenType.ProstateBiopsy,
        status: initialOrder?.status || OrderStatus.Draft,
      } as SamdOrderDetailFieldsFragment;
    }, [getValues, initialOrder]);

  useEffect(() => {
    if (initialOrder.id) {
      trigger();
    }
  }, [initialOrder, trigger]);

  useEffect(() => {
    const result = processFetcherResponse(fetcher.data, fetcher.state);

    if (result) {
      if (result.type === 'success') {
        notifySuccess(result.message);
        if (result.closeDialog) {
          onClose();
        }
      } else if (result.type === 'error') {
        notifyError(result.message);
      }
    }
  }, [fetcher.data, fetcher.state, onClose]);

  const isNewOrder = !initialOrder.id;
  const isDraftOrder = initialOrder.status === OrderStatus.Draft;

  const handleSaveOrder = async () => {
    const orderData = getOrderFromFormData();

    if (!orderData.id) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.CREATE,
        location,
        orderData,
      );
    } else {
      submitOrderAction(
        fetcher.submit,
        OrderAction.UPDATE,
        location,
        orderData,
      );
    }
  };

  const handleSubmitOrder = async () => {
    const orderData = getOrderFromFormData();
    const action =
      orderData.status === OrderStatus.Draft
        ? OrderAction.SUBMIT
        : OrderAction.RESUBMIT;

    submitOrderAction(fetcher.submit, action, location, orderData);
  };

  const handleDiscard = async () => {
    if (isDraftOrder && !isNewOrder) {
      submitOrderAction(
        fetcher.submit,
        OrderAction.DELETE,
        location,
        undefined,
      );
    } else {
      notifySuccess('Order discarded successfully.');
      onClose();
    }
  };

  const formTitle = `${isNewOrder ? 'Create' : 'Edit'} ${SpecimenTypeUtils.getSpecimenTypeName(SpecimenType.ProstateBiopsy)} Test Order`;

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(handleSubmitOrder)}
      noValidate
      sx={{
        width: '100%',
        maxWidth: FORM_CONSTANTS.WIDTH,
        px: { xs: 0, sm: 2, md: 0 },
      }}
    >
      <Typography
        variant='h5'
        sx={{
          display: 'block',
          fontWeight: theme.typography.fontWeightBold,

          fontSize: {
            xs: theme.typography.h6.fontSize,
            sm: theme.typography.h5.fontSize,
          },
        }}
      >
        {formTitle}
      </Typography>
      <Typography
        variant='caption'
        sx={{
          display: 'block',
          color: 'text.secondary',
          mb: 2,
        }}
      >
        *Indicates a required field
      </Typography>
      <Stack spacing={{ xs: 2, sm: 2 }}>
        <PatientIdField control={control} errors={errors} />
        <SamdPatientPhiFormSection control={control} errors={errors} />
        <PatientAgeField control={control} errors={errors} />
        <GleasonScoreFields
          control={control}
          errors={errors}
          trigger={trigger}
        />
        <TStageField control={control} errors={errors} />
        <FormActions
          showSubmitButton={true}
          showSaveButton={false}
          showDraftActionButtons={isDraftOrder || isNewOrder}
          onValidateBeforeSubmit={async () => trigger()}
          onConfirmSubmit={handleSubmitOrder}
          onSaveAsDraft={handleSaveOrder}
          onDiscard={handleDiscard}
          requireDiscardConfirmation={!isNewOrder}
          onDiscardSuccess={() => {
            notifySuccess('Order discarded successfully.');
            onClose();
          }}
        />
      </Stack>
    </Box>
  );
};
